/**
 * OAuth Popup Helper
 *
 * Handles popup window creation and communication
 */

/**
 * Open OAuth popup window
 *
 * @param {string} url - OAuth authorization URL
 * @param {string} title - Popup window title
 * @returns {Window} Popup window reference
 */
export const openPopup = (url, title = 'OAuth Login') => {
	const width = 500;
	const height = 600;
	const left = window.screenX + (window.outerWidth - width) / 2;
	const top = window.screenY + (window.outerHeight - height) / 2;

	return window.open(
		url,
		title,
		`width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`,
	);
};

/**
 * Poll popup for OAuth callback
 *
 * @param {Window} popup - Popup window reference
 * @param {string} redirectUri - Expected redirect URI
 * @param {Function} onSuccess - Success callback (code, state)
 * @param {Function} onError - Error callback
 */
export const pollPopup = (popup, redirectUri, onSuccess, onError) => {
	const pollInterval = setInterval(() => {
		try {
			// Check if popup closed manually
			if (popup.closed) {
				clearInterval(pollInterval);
				onError(new Error('Authentication cancelled'));
				return;
			}

			// Try to access popup URL (throws error if cross-origin)
			const popupUrl = popup.location.href;

			// Check if redirected to our callback URL
			if (popupUrl.includes(redirectUri)) {
				clearInterval(pollInterval);

				// Parse URL parameters
				const url = new URL(popupUrl);
				const code = url.searchParams.get('code');
				const error = url.searchParams.get('error');
				const errorDescription = url.searchParams.get('error_description');
				const state = url.searchParams.get('state');

				// Close popup
				popup.close();

				if (error) {
					onError(new Error(errorDescription || error));
				} else if (code) {
					onSuccess({ code, state });
				} else {
					onError(new Error('No authorization code received'));
				}
			}
		} catch (e) {
			// Cross-origin error - popup still on OAuth provider domain
			// Continue polling
		}
	}, 500);

	// Cleanup on timeout (5 minutes)
	setTimeout(() => {
		clearInterval(pollInterval);
		if (!popup.closed) {
			popup.close();
			onError(new Error('Authentication timeout'));
		}
	}, 300000);
};

/**
 * Generate random state parameter for CSRF protection
 *
 * @returns {string}
 */
export const generateState = () => {
	return (
		Math.random().toString(36).substring(2, 15) +
		Math.random().toString(36).substring(2, 15)
	);
};

/**
 * Build OAuth authorization URL
 *
 * @param {string} baseUrl - OAuth provider's authorization endpoint
 * @param {Object} params - Query parameters
 * @returns {string}
 */
export const buildAuthUrl = (baseUrl, params) => {
	const url = new URL(baseUrl);
	Object.keys(params).forEach((key) => {
		if (params[key] !== undefined && params[key] !== null) {
			url.searchParams.append(key, params[key]);
		}
	});
	return url.toString();
};
